/**
 * useSeo — zero-dependency SEO composable for SPA head management.
 *
 * Usage (static page):
 *   useSeo({ title: "Grupos de Salud", description: "..." })
 *
 * Usage (dynamic page, call setSeo() after data loads):
 *   const { setSeo } = useSeo();
 *   watch(doctor, (d) => { if (d) setSeo({ title: d.name, ... }); });
 */

import { COMPANY } from "@/constants/company";
import { onMounted } from "vue";

export interface SeoOptions {
  title?: string;
  description?: string;
  /** Absolute URL for og:image. Falls back to default OG image. */
  image?: string;
  /** Override canonical URL. Defaults to current window.location.href. */
  url?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  /** Schema.org JSON-LD object or array of objects. */
  jsonLd?: object | object[];
}

const SITE_NAME = "Medkus";
const BASE_URL = "https://medkus.com";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  "Medkus es la plataforma para encontrar y conectar con especialistas en medicina integrativa, nutricionistas, fisioterapeutas, psicólogos, ostéopatas y terapeutas de bienestar.";

// ── Internal helpers ────────────────────────────────────────────────────────

function setMeta(nameOrProp: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeJsonLd() {
  document.querySelectorAll('script[data-seo="jsonld"]').forEach((s) => s.remove());
}

function injectJsonLd(data: object | object[]) {
  removeJsonLd();
  const items = Array.isArray(data) ? data : [data];
  items.forEach((item) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", "jsonld");
    s.textContent = JSON.stringify(item);
    document.head.appendChild(s);
  });
}

// ── Public API ──────────────────────────────────────────────────────────────

export function setSeo(opts: SeoOptions = {}) {
  const {
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    url,
    type = "website",
    noindex = false,
    jsonLd,
  } = opts;

  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = url || `${BASE_URL}${window.location.pathname}`;

  // Basic
  document.title = fullTitle;
  setMeta("description", description);
  setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

  // Canonical
  setLink("canonical", canonicalUrl);

  // Open Graph
  setMeta("og:site_name", SITE_NAME, "property");
  setMeta("og:title", fullTitle, "property");
  setMeta("og:description", description, "property");
  setMeta("og:url", canonicalUrl, "property");
  setMeta("og:image", image, "property");
  setMeta("og:type", type, "property");

  // Twitter Card
  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", fullTitle);
  setMeta("twitter:description", description);
  setMeta("twitter:image", image);

  // JSON-LD
  if (jsonLd) injectJsonLd(jsonLd);
  else removeJsonLd();
}

/** Composable: calls setSeo() in onMounted for static pages. */
export function useSeo(opts?: SeoOptions) {
  onMounted(() => setSeo(opts ?? {}));
  return { setSeo };
}

// ── Reusable JSON-LD builders ───────────────────────────────────────────────

export const jsonLd = {
  webSite(): object {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: BASE_URL,
      description: DEFAULT_DESCRIPTION,
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${BASE_URL}/search?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    };
  },

  organization(): object {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      legalName: COMPANY.name,
      taxID: COMPANY.rfc,
      telephone: COMPANY.phone,
      url: BASE_URL,
      logo: `${BASE_URL}/logo.png`,
      sameAs: [],
    };
  },

  medicalBusiness(opts: {
    name: string;
    description?: string;
    image?: string;
    specialty?: string;
    url?: string;
  }): object {
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: opts.name,
      description: opts.description || "",
      image: opts.image || DEFAULT_IMAGE,
      url: opts.url || BASE_URL,
      ...(opts.specialty ? { knowsAbout: opts.specialty } : {}),
    };
  },

  blogArticle(opts: {
    title: string;
    description?: string;
    image?: string;
    url?: string;
    datePublished?: string;
    authorName?: string;
  }): object {
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: opts.title,
      description: opts.description || "",
      image: opts.image || DEFAULT_IMAGE,
      url: opts.url || BASE_URL,
      datePublished: opts.datePublished || "",
      author: {
        "@type": "Organization",
        name: opts.authorName || SITE_NAME,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png` },
      },
    };
  },

  virtualEvent(opts: {
    name: string;
    description?: string;
    startDate?: string;
    url?: string;
  }): object {
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name: opts.name,
      description: opts.description || "",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "VirtualLocation",
        url: opts.url || BASE_URL,
      },
      ...(opts.startDate ? { startDate: opts.startDate } : {}),
      organizer: { "@type": "Organization", name: SITE_NAME, url: BASE_URL },
    };
  },

  softwareApp(): object {
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: SITE_NAME,
      applicationCategory: "HealthApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "30", priceCurrency: "EUR" },
      description:
        "Plataforma de gestión para wellness médico y salud integrativa: motor de reservas en tiempo real (Naavi), historia clínica cifrada, CRM, ventas, comunicación, IA e informes.",
    };
  },
};
